import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import {
  ArrowRight,
  Download,
  FileText,
  Inbox,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { ExternalFile } from "../../../../hooks/useExternalFiles";
import { cn } from "../../../../lib/utils";

export type ExternalImportMode = "sample" | "guidance";

interface ExternalFilesImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ExternalImportMode;
  files: ExternalFile[];
  loading: boolean;
  downloading: Set<number>;
  onDownload: (file: ExternalFile) => void;
  onImport: (files: ExternalFile[]) => Promise<void>;
}

const TITLES: Record<ExternalImportMode, string> = {
  sample: "Import sample evidence from Optro",
  guidance: "Import general evidence from Optro",
};

const formatBytes = (size: string | null): string | null => {
  if (!size) return null;
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return size;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

const fileExtension = (file: ExternalFile): string => {
  const name = file.name ?? "";
  const dot = name.lastIndexOf(".");
  if (dot > -1 && dot < name.length - 1) {
    return name.slice(dot + 1).toLowerCase();
  }
  return file.type ?? "";
};

export const ExternalFilesImportDialog = ({
  open,
  onOpenChange,
  mode,
  files,
  loading,
  downloading,
  onDownload,
  onImport,
}: ExternalFilesImportDialogProps) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedIds(new Set());
      setLastSelectedId(null);
      setSubmitting(false);
    }
  }, [open]);

  const filteredFiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return files;
    return files.filter((file) =>
      (file.name ?? `file ${file.id}`).toLowerCase().includes(query),
    );
  }, [files, search]);

  const visibleSelectedCount = useMemo(
    () => filteredFiles.filter((f) => selectedIds.has(f.id)).length,
    [filteredFiles, selectedIds],
  );

  const masterState: boolean | "indeterminate" = useMemo(() => {
    if (filteredFiles.length === 0) return false;
    if (visibleSelectedCount === 0) return false;
    if (visibleSelectedCount === filteredFiles.length) return true;
    return "indeterminate";
  }, [filteredFiles.length, visibleSelectedCount]);

  const toggleRow = useCallback(
    (fileId: number, shift: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (
          shift &&
          lastSelectedId !== null &&
          lastSelectedId !== fileId &&
          filteredFiles.length > 0
        ) {
          const ids = filteredFiles.map((f) => f.id);
          const from = ids.indexOf(lastSelectedId);
          const to = ids.indexOf(fileId);
          if (from > -1 && to > -1) {
            const [a, b] = from < to ? [from, to] : [to, from];
            const willSelect = !prev.has(fileId);
            for (let i = a; i <= b; i++) {
              if (willSelect) next.add(ids[i]);
              else next.delete(ids[i]);
            }
            return next;
          }
        }
        if (next.has(fileId)) next.delete(fileId);
        else next.add(fileId);
        return next;
      });
      setLastSelectedId(fileId);
    },
    [filteredFiles, lastSelectedId],
  );

  const toggleMaster = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allVisibleSelected =
        filteredFiles.length > 0 && filteredFiles.every((f) => prev.has(f.id));
      if (allVisibleSelected) {
        for (const f of filteredFiles) next.delete(f.id);
      } else {
        for (const f of filteredFiles) next.add(f.id);
      }
      return next;
    });
  }, [filteredFiles]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const handleImport = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const selected = files.filter((f) => selectedIds.has(f.id));
    if (selected.length === 0) return;
    setSubmitting(true);
    try {
      await onImport(selected);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }, [files, onImport, onOpenChange, selectedIds]);

  const hasSelection = selectedIds.size > 0;
  const selectedCount = selectedIds.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 rounded-lg overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200">
          <DialogTitle className="text-base font-semibold tracking-tight text-stone-900">
            {TITLES[mode]}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Select files to import. Shift-click to select a range.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pt-4 pb-3 border-b border-stone-200">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files by name…"
              className="pl-9 pr-9 h-9 rounded-md"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div
          className={cn(
            "flex items-center gap-3 px-6 h-11 border-b border-stone-200 text-xs transition-colors",
            hasSelection ? "bg-stone-900 text-stone-100" : "bg-stone-50/60",
          )}
        >
          <Checkbox
            checked={masterState}
            onCheckedChange={toggleMaster}
            disabled={filteredFiles.length === 0}
            aria-label="Select all visible files"
            className={cn(
              hasSelection &&
                "border-stone-400 data-[state=checked]:bg-white data-[state=checked]:text-stone-900",
            )}
          />
          {hasSelection ? (
            <>
              <span className="font-medium">{selectedCount} selected</span>
              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto text-stone-400 hover:text-white transition-colors"
              >
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span className="text-stone-600">
                {filteredFiles.length}{" "}
                {filteredFiles.length === 1 ? "file" : "files"}
                {search && files.length !== filteredFiles.length
                  ? ` of ${files.length}`
                  : ""}
              </span>
              <span className="ml-auto text-stone-400">
                Tick rows or use the master checkbox to select files
              </span>
            </>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-stone-500 text-sm">
              <Loader2 size={16} className="mr-2 animate-spin" />
              Loading files…
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-stone-500">
              <Inbox size={28} className="text-stone-300 mb-3" />
              <p className="text-sm">
                {files.length === 0
                  ? "No external files found"
                  : "No files match your search"}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {filteredFiles.map((file) => {
                const isSelected = selectedIds.has(file.id);
                const ext = fileExtension(file);
                const size = formatBytes(file.size);
                const isDownloading = downloading.has(file.id);
                return (
                  <li
                    key={file.id}
                    className={cn(
                      "group flex items-center gap-3 px-6 py-3 transition-colors border-l-2 cursor-pointer select-none",
                      isSelected
                        ? "border-l-sky-500 bg-sky-50/40 hover:bg-sky-50/70 ring-1 ring-inset ring-stone-300"
                        : "border-l-transparent hover:bg-stone-50",
                    )}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest("button") || target.closest("input"))
                        return;
                      toggleRow(file.id, e.shiftKey);
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(file.id, false)}
                      aria-label={`Select ${file.name ?? `file ${file.id}`}`}
                    />
                    <div
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-md shrink-0 transition-colors",
                        isSelected
                          ? "bg-sky-100 text-sky-700"
                          : "bg-stone-100 text-stone-500",
                      )}
                    >
                      <FileText size={15} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-stone-900 truncate">
                        {file.name ?? `File ${file.id}`}
                      </p>
                      <p className="text-[11px] uppercase tracking-wide text-stone-500 mt-0.5">
                        {[ext, size].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-stone-500 hover:text-stone-900"
                          disabled={isDownloading}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownload(file);
                          }}
                        >
                          {isDownloading ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Download</TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-stone-200 bg-stone-50/60">
          <Button
            variant="ghost"
            size="default"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            onClick={handleImport}
            disabled={selectedCount === 0 || submitting}
          >
            {submitting ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <ArrowRight size={14} className="mr-2" />
            )}
            {submitting
              ? "Importing…"
              : selectedCount === 0
                ? "Continue"
                : `Continue with ${selectedCount} ${selectedCount === 1 ? "file" : "files"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
